import { type ReactElement, useEffect, useId, useRef } from 'react';

import {
  BookOpenIcon,
  CalendarPlusIcon,
  ClockCheckIcon,
  FlagIcon,
  GlobeIcon,
  GlobePointedIcon,
  HelpIcon,
  LightningIcon,
  LoadingIcon,
  MergeIcon,
  SplitIcon,
  TranslateIcon,
  UserIcon,
  UserManagerIcon,
  UsersIcon,
  VoiceIcon,
} from '@braskai/icons';
import { Button, Icon, Typography } from '@braskai/ui-kit';

import type { NewUserQuizPriority } from '@/shared/api/newUserQuiz';

import RaskLogo from '@/shared/assets/images/rask.svg';

import type { NewUserQuizFormState, QuizOption, SingleAnswerKey, SingleAnswerValue } from '../lib/types';

import {
  getNewUserQuizPriorityOptions,
  getNewUserQuizQuestions,
  NEW_USER_QUIZ_TEST_IDS,
  NEW_USER_QUIZ_TOTAL_STEPS,
} from '../lib/constants';
import { getNewUserQuizLocalization, type NewUserQuizLocalization } from '../lib/i18n';
import {
  Actions,
  ErrorContent,
  Logo,
  OptionCardContent,
  OptionIcon,
  OptionInput,
  OptionLabel,
  OptionsGrid,
  OptionText,
  ProgressRoot,
  ProgressStep,
  QuestionArea,
  QuestionContent,
  QuestionHeader,
  QuestionTitle,
  QuizContent,
  QuizRoot,
} from './NewUserQuizQuestions.styled';

type OptionCardProps = {
  label: string;
  name: string;
  'data-testid': string;
  description?: string;
  disabled: boolean;
  icon?: ReactElement;
  selected: boolean;
  selectionMode: 'multiple' | 'single';
  value: string;
  onSelect: () => void;
};

const OptionCard = ({
  label,
  name,
  'data-testid': dataTestId,
  description,
  disabled,
  icon,
  selected,
  selectionMode,
  value,
  onSelect,
}: OptionCardProps) => (
  <OptionLabel>
    <OptionInput
      name={name}
      checked={selected}
      data-testid={dataTestId}
      disabled={disabled}
      type={selectionMode === 'single' ? 'radio' : 'checkbox'}
      value={value}
      onChange={onSelect}
      onClick={() => {
        if (selectionMode === 'single' && selected) {
          onSelect();
        }
      }}
    />
    <OptionCardContent $selected={selected}>
      <OptionText>
        {icon ? (
          <OptionIcon>
            <Icon aria-hidden="true" color="grey.500" component={icon} size={20} />
          </OptionIcon>
        ) : null}
        <Typography color="grey.900" variant="body-2-md">
          {label}
        </Typography>
        {description ? (
          <Typography color="grey.500" variant="caption-1-md">
            {description}
          </Typography>
        ) : null}
      </OptionText>
    </OptionCardContent>
  </OptionLabel>
);

const OPTION_ICONS: Partial<Record<string, ReactElement>> = {
  changes_by_project: <SplitIcon />,
  collaborate_with_team_or_reviewers: <UsersIcon />,
  consistent_voice_and_terminology: <VoiceIcon />,
  continuously: <LoadingIcon />,
  fast_results_no_setup: <LightningIcon />,
  four_to_ten: <GlobePointedIcon />,
  integration_into_product_or_workflow: <MergeIcon />,
  just_me: <UserIcon />,
  large_team_5_plus: <UserManagerIcon />,
  not_sure_yet: <HelpIcon />,
  occasionally_quarterly: <CalendarPlusIcon />,
  regularly_monthly: <ClockCheckIcon />,
  small_team_2_5: <UsersIcon />,
  ten_plus: <GlobeIcon />,
  two_to_three: <BookOpenIcon />,
  one: <TranslateIcon />,
  one_time_project: <FlagIcon />,
};

const Progress = ({ ariaLabel, currentStep }: { ariaLabel: string; currentStep: number }) => (
  <ProgressRoot aria-label={ariaLabel}>
    {Array.from({ length: NEW_USER_QUIZ_TOTAL_STEPS }, (_, index) => (
      <ProgressStep key={index} $active={index === currentStep} />
    ))}
  </ProgressRoot>
);

type NewUserQuizQuestionsProps = {
  answers: NewUserQuizFormState;
  canContinueAnyway: boolean;
  currentStep: number;
  hasSubmitError: boolean;
  isAdvancing: boolean;
  isSubmitting: boolean;
  localization?: NewUserQuizLocalization;
  onBack: () => void;
  onContinueAnyway: () => void;
  onSelectSingle: (key: SingleAnswerKey, value: SingleAnswerValue) => void;
  onSubmit: () => void;
  onTogglePriority: (value: NewUserQuizPriority) => void;
};

type DisplayOptionValue = NewUserQuizPriority | SingleAnswerValue;
type DisplayOption = { value: DisplayOptionValue } & Omit<QuizOption, 'value'>;

export const NewUserQuizQuestions = ({
  answers,
  canContinueAnyway,
  currentStep,
  hasSubmitError,
  isAdvancing,
  isSubmitting,
  localization = getNewUserQuizLocalization(),
  onBack,
  onContinueAnyway,
  onSelectSingle,
  onSubmit,
  onTogglePriority,
}: NewUserQuizQuestionsProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previousStepRef = useRef(currentStep);
  const radioGroupName = useId();
  const { copy, locale } = localization;
  const questions = getNewUserQuizQuestions(copy);
  const isPriorityStep = currentStep === NEW_USER_QUIZ_TOTAL_STEPS - 1;
  const question = isPriorityStep ? null : questions[currentStep];
  const title = question?.title ?? copy.priorityQuestion.title;
  const subtitle = question?.subtitle ?? (isPriorityStep ? copy.priorityQuestion.subtitle : undefined);
  const desktopPaddingTop = question?.desktopPaddingTop ?? 80;
  const desktopWidth = question?.desktopWidth ?? 864;
  const options: readonly DisplayOption[] = question?.options ?? getNewUserQuizPriorityOptions(copy);

  useEffect(() => {
    if (previousStepRef.current !== currentStep) {
      headingRef.current?.focus();
      previousStepRef.current = currentStep;
    }
  }, [currentStep]);

  const isSelected = (value: DisplayOptionValue) => {
    if (isPriorityStep) {
      return answers.priorities.includes(value as NewUserQuizPriority);
    }

    return question ? answers[question.key] === value : false;
  };

  const handleOptionClick = (value: DisplayOptionValue) => {
    if (isPriorityStep) {
      onTogglePriority(value as NewUserQuizPriority);
      return;
    }

    if (question) {
      onSelectSingle(question.key, value as SingleAnswerValue);
    }
  };

  return (
    <QuizRoot $desktopPaddingTop={desktopPaddingTop} data-testid={NEW_USER_QUIZ_TEST_IDS.quiz} lang={locale}>
      <QuizContent $desktopWidth={desktopWidth}>
        <Logo alt="Rask" src={RaskLogo} />

        <QuestionArea>
          <QuestionContent>
            <QuestionHeader>
              <QuestionTitle
                id="new-user-quiz-question-title"
                aria-live="polite"
                color="grey.900"
                component="h1"
                ref={headingRef}
                tabIndex={-1}
                variant="title-2-md"
              >
                {title}
              </QuestionTitle>
              {subtitle ? (
                <Typography color="grey.500" variant="body-2-md">
                  {subtitle}
                </Typography>
              ) : null}
            </QuestionHeader>

            <OptionsGrid
              $columns={Math.min(options.length, 4)}
              aria-labelledby="new-user-quiz-question-title"
              role={isPriorityStep ? 'group' : 'radiogroup'}
            >
              {options.map((option) => (
                <OptionCard
                  key={String(option.value)}
                  label={option.label}
                  name={isPriorityStep ? `${radioGroupName}-priorities` : radioGroupName}
                  data-testid={NEW_USER_QUIZ_TEST_IDS.option(option.value)}
                  description={option.description}
                  disabled={isAdvancing || isSubmitting}
                  icon={OPTION_ICONS[String(option.value)]}
                  selected={isSelected(option.value)}
                  selectionMode={isPriorityStep ? 'multiple' : 'single'}
                  value={String(option.value)}
                  onSelect={() => handleOptionClick(option.value)}
                />
              ))}
            </OptionsGrid>

            <Actions>
              {currentStep > 0 ? (
                <Button
                  data-testid={NEW_USER_QUIZ_TEST_IDS.back}
                  disabled={isSubmitting}
                  size="sm"
                  variant="text"
                  onClick={onBack}
                >
                  {copy.controls.back}
                </Button>
              ) : null}
              {isPriorityStep ? (
                <Button
                  data-testid={NEW_USER_QUIZ_TEST_IDS.continue}
                  disabled={answers.priorities.length === 0}
                  isLoading={isSubmitting}
                  size="sm"
                  variant="primary"
                  onClick={onSubmit}
                >
                  {copy.controls.continue}
                </Button>
              ) : null}
            </Actions>

            {hasSubmitError ? (
              <ErrorContent>
                <Typography color="error.700" data-testid={NEW_USER_QUIZ_TEST_IDS.error} role="alert" variant="body-2">
                  {copy.controls.submitError}
                </Typography>
                {canContinueAnyway ? (
                  <Button
                    data-testid={NEW_USER_QUIZ_TEST_IDS.continueAnyway}
                    size="sm"
                    variant="text"
                    onClick={onContinueAnyway}
                  >
                    {copy.controls.continueAnyway}
                  </Button>
                ) : null}
              </ErrorContent>
            ) : null}
          </QuestionContent>
        </QuestionArea>
      </QuizContent>

      <Progress
        ariaLabel={copy.controls.progress(currentStep + 1, NEW_USER_QUIZ_TOTAL_STEPS)}
        currentStep={currentStep}
      />
    </QuizRoot>
  );
};
